import { Close, Search } from "@mui/icons-material";
import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import ClinicFilter from "../../../components/filter/ClinicFilter";
import SearchBar from "../../../components/filter/SearchBar";
import UserActiveAppointmentFilter from "../../../components/filter/UserActiveAppointmentFilter";
import UserActiveStatusFilter from "../../../components/filter/UserActiveStatusFilter";
import UserNoPetsFilter from "../../../components/filter/UserNoPetsFilter";
import ErrorFullScreenMessage from "../../../components/fullScreenMessage/ErrorFullScreenMessage";
import FullScreenMessageCore from "../../../components/fullScreenMessage/FullScreenMessageCore";
import { ClinicUsersContext } from "../../../contexts/ClinicUsers";
import ToggleDrawer from "../../../contexts/ToggleDrawer";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import useUser from "../../../hooks/useUser";
import { useUserFiltering } from "../utils/Functions";
import FiltersDesktopUserListingPage from "./components/FiltersDesktopUserListingPage";
import NewUserFormComponent from "./components/NewUserFormComponent";
import TableRowBody from "./components/TableRowBody";
import TableRowHead from "./components/TableRowHead";
import useGetTableConst from "./hooks/useGetTableConst";
import { IMPORT_USERS_PAGE } from "../../../enums/UrlPaths";

export const userTableParams = {
  wildcard: "q",
  page: "page",
  rowsPerPage: "rows",
  clinic: "clinicId",
  active: "active",
  withoutPets: "withoutPets",
  withoutReservations: "withoutReservations",
  sort: "sort",
};
export const filterComponents = [
  {
    component: <ClinicFilter param={userTableParams.clinic} />,
    label: "Clinic",
  },
  {
    component: <UserActiveStatusFilter param={userTableParams.active} />,
    label: "ActiveStatus",
  },
  {
    component: <UserNoPetsFilter param={userTableParams.withoutPets} />,
    label: "PetsFilterLabel",
  },
  {
    component: (
      <UserActiveAppointmentFilter
        param={userTableParams.withoutReservations}
      />
    ),
    label: "Appointments",
  },
];
export default function DesktopUserListingPage() {
  const { roles } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { header, tableFields } = useGetTableConst();
  const { t } = useTranslation();
  const { getTableData, getPagination, getLoading, getError } =
    React.useContext(ClinicUsersContext);
  const isLoading = getLoading();
  const error = getError();
  const { total: totalCount } = getPagination();
  const { preferance } = useUser();
  const [rowsPerPage, setRowsPerPage] = React.useState(
    searchParams.get(userTableParams.rowsPerPage) ||
      preferance?.clinicUsersTableRowsPerPage ||
      10,
  );
  // Get current sort from URL or use defaults
  const sortParam = searchParams.get("sort") || "id=asc";
  const [sortField, sortDirection] = sortParam.split("=");
  const orderBy = sortField;
  const order = sortDirection;
  const [filtersDialogOpen, setFiltersDialogOpen] = React.useState(false);
  const isDesktop = useIsDesktop();

  const handleRequestSort = (event, property) => {
    const currentOrder = order;
    const currentOrderBy = orderBy;

    const isAsc = currentOrderBy === property && currentOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    const params = new URLSearchParams(searchParams);
    params.set("sort", `${property}=${newOrder}`);
    params.set(userTableParams.page, "0");

    setSearchParams(params);
  };
  const handleDialogOpen = React.useCallback(() => {
    setFiltersDialogOpen(true);
  }, []);

  const handleDialogClose = React.useCallback(() => {
    setFiltersDialogOpen(false);
  }, []);

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  function getPage() {
    const num = searchParams.get(userTableParams.page);
    return num < 0 ? 0 : num;
  }
  const [page, setPage] = React.useState(getPage());
  const { handleWildCardData, resetFilter } = useUserFiltering();

  const handleChangeRowsPerPage = (e) => {
    const num = Number(e.target.value);
    setRowsPerPage(num);

    const params = new URLSearchParams(searchParams);
    params.set(userTableParams.rowsPerPage, num);
    params.set(userTableParams.page, 0);
    setSearchParams(params);
  };

  const handleChangePage = (e, newPage) => {
    changePageNumber(newPage);
  };

  function changePageNumber(num) {
    const params = new URLSearchParams(searchParams);
    params.delete(userTableParams.page);
    params.set(userTableParams.page, Number(num));
    setSearchParams(params);
  }

  function getPage() {
    const page = Number(searchParams.get(userTableParams.page));
    if (page < 0) {
      searchParams.set(userTableParams.page, 0);
      setSearchParams(searchParams);
      return 0;
    } else {
      return page;
    }
  }

  return roles.user ? (
    <ErrorFullScreenMessage />
  ) : (
    <Wrapper>
      <MUI.Stack spacing={3} direction="column">
        <MUI.Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <MUI.Stack direction={"row"} spacing={2}>
            <NewUser />
            <ImportUsersNavigation />
          </MUI.Stack>
          {roles.master && isDesktop && (
            <MUI.Button
              startIcon={<Close />}
              color="error"
              onClick={resetFilter}
              variant="contained"
              size="medium"
              data-testid="users.page.reset-filters-button"
            >
              {t("Reset")}
            </MUI.Button>
          )}
        </MUI.Stack>
        <SearchBar
          key={"search-bar-for-clinic-users"}
          lsVal={userTableParams.wildcard}
          getData={(value) => handleWildCardData(value)}
          margin={3}
          testId="users.page.search-input"
        />
        {roles.master && (
          <MUI.Box sx={{ mb: 2 }}>
            <FiltersDesktopUserListingPage
              filterComponents={filterComponents}
              dialogOpen={filtersDialogOpen}
              onDialogOpen={handleDialogOpen}
              onDialogClose={handleDialogClose}
              onReset={resetFilter}
            />
          </MUI.Box>
        )}
      </MUI.Stack>

      <MUI.Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {isLoading && (
          <MUI.Backdrop
            open={isLoading}
            sx={{
              position: "absolute",
              backgroundColor: "transparent",
            }}
          >
            <MUI.CircularProgress color="inherit" />
          </MUI.Backdrop>
        )}
        {error && <ErrorFullScreenMessage />}
        {getTableData()?.length > 0 ? (
          <MUI.Box
            sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
          >
            <MUI.TableContainer sx={{ flexGrow: 1, overflowX: "auto" }}>
              <MUI.Table sx={{ minWidth: 750, tableLayout: "auto" }}>
                <TableRowHead
                  header={header}
                  order={order}
                  orderBy={orderBy}
                  createSortHandler={(id) => createSortHandler(id)}
                  testIdPrefix="users.page.table"
                />
                <TableRowBody
                  visibleRows={getTableData()}
                  tableFields={tableFields}
                  testIdPrefix="users.page.table"
                />
              </MUI.Table>
            </MUI.TableContainer>

            <MUI.TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              labelRowsPerPage={t("RowsPerPage")}
              page={getPage()}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              backIconButtonProps={{
                title: t("PreviousPage"),
                "aria-label": t("PreviousPage"),
              }}
              nextIconButtonProps={{
                title: t("NextPage"),
                "aria-label": t("NextPage"),
              }}
              data-testid="users.page.pagination"
              sx={
                !isDesktop
                  ? {
                      "& .MuiTablePagination-displayedRows": {
                        fontSize: "0.7rem",
                      },
                      "& .MuiTablePagination-actions": {
                        margin: 0,
                      },
                      "& .MuiTablePagination-selectLabel": {
                        fontSize: "0.7rem",
                      },
                      "& .MuiTablePagination-select": {
                        fontSize: "0.7rem",
                      },
                      "& .MuiTablePagination-actions .MuiSvgIcon-root": {
                        fontSize: "1.1rem", // set icon size
                      },
                    }
                  : {}
              }
            />
          </MUI.Box>
        ) : (
          !isLoading && <NoData />
        )}
      </MUI.Box>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <MUI.Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        px: 2,
        pt: 12,
        width: "100vw",
        maxWidth: "100%",
      }}
    >
      {children}
    </MUI.Box>
  );
}

function ImportUsersNavigation() {
  const { t } = useTranslation();
  const { roles } = useUser();
  const navigate = useNavigate();
  function handleNavigation() {
    navigate(IMPORT_USERS_PAGE);
  }

  if (roles.adminOrEmployee) {
    return (
      <MUI.Button
        variant="contained"
        onClick={() => handleNavigation()}
        data-testid="users.page.import-users-button"
      >
        {t("ImportUsers")}
      </MUI.Button>
    );
  }
}

function NewUser() {
  return (
    <MUI.Box>
      <ToggleDrawer>
        <NewUserFormComponent />
      </ToggleDrawer>
    </MUI.Box>
  );
}

function NoData() {
  return (
    <MUI.Stack
      sx={{ minHeight: 580 }}
      key={"clinics-users-table-pagination-key"}
    >
      <FullScreenMessageCore
        withoutWrapper={false}
        shouldCenter={false}
        centerRelative={true}
        containerBoxShadow={undefined}
        title={"NoSearchResults"}
        icon={<Search className="Mui-fullscreen-icon" />}
        containerPadding={3}
        testId="user.search.not-found"
      />
    </MUI.Stack>
  );
}
